import React from 'react';

import { Button, Props as ButtonProps } from '../Button';
import { useSlug } from '../hooks/useSlug';
import { classes } from '../utils';

import styles from './IconButton.module.css';

type PhoenixIconButtonProps = Omit<
  ButtonProps,
  'size' | 'iconPosition' | 'ActionIcon' | 'fullWidth' | 'Icon' | 'loading'
> & {
  appearance?: 'default' | 'outline';
  colorMode?: 'light' | 'dark';
  size?: 'small' | 'tiny' | 'default';
};

export type SundaeButtonProps = Omit<
  ButtonProps,
  'bold' | 'ActionIcon' | 'fullWidth' | 'size' | 'loading'
> & {
  size?: 'small' | 'tiny' | 'default';
  title?: string;
  /** @deprecated use version=phoenix and outline prop */
  variant?: 'default' | 'outlined';
};

export type IconButtonProps = SundaeButtonProps & PhoenixIconButtonProps;

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButtonComponent(props: IconButtonProps, ref) {
    const {
      colorMode = 'light',
      appearance = 'default',
      variant = 'default',
      size = 'default',
      type = 'button',
      title = '',
      disabled = false,
      linkUrl,
      openLinkInNewWindow,
      name = 'icon-button',
      version,
    } = props;

    const cx =
      version === 'phoenix'
        ? classes(
            [
              styles[`iconButton-${props.version}`],
              styles[appearance],
              styles[size],
              styles[colorMode],
              styles.onlyIcon,
            ],
            {
              name,
              className: props.className,
              ...props,
            }
          )
        : classes([styles.wrapper, styles[variant], styles[size]], {
            ...props,
            name,
          });

    const componentName = useSlug(name);

    const noop = React.useCallback(() => undefined, []);

    /* Just to be sure, lets leave alone the pre-phoenix behavior, but
    really this should all be defaulting to use `Button` behavior that
    exists, and not be implmenting it by itself.
    TODO: when removing old sundae buttons, delete all that follows
    */
    if (version === 'phoenix') {
      return (
        <Button
          {...(props as ButtonProps)}
          name={name}
          className={cx}
          ref={ref}
          iconPosition="left"
        />
      );
    }

    // remove size from props since we are handling manually
    // via classnames above
    const { size: _s, children, ...rest } = props;

    // if we have a link
    if (linkUrl) {
      return (
        <a
          title={title || ''}
          href={linkUrl}
          className={styles.link}
          rel="noopener noreferrer"
          target={openLinkInNewWindow ? '_blank' : undefined}
        >
          <button
            {...rest}
            disabled={disabled}
            title={title}
            className={cx}
            data-component={componentName}
            type={type}
            aria-label={title || ''}
            onClick={noop}
            ref={ref}
          >
            {children}
          </button>
        </a>
      );
    }

    return (
      <button
        {...rest}
        disabled={disabled}
        title={title}
        className={cx}
        data-component={componentName}
        type={type}
        aria-label={title || ''}
        ref={ref}
      >
        {children}
      </button>
    );
  }
);
